import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core'
import { MatAutocompleteTrigger } from '@angular/material/autocomplete'
import { NavigationEnd, Params, Router } from '@angular/router'
import {
  BookmarksState,
  BookmarkType,
  BookmarkTypeToLabel,
  DeleteBookmark,
  IBookmarkItem,
  Logout,
  UserAccountState
} from '@lla-platform/users/users-data-access'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Store } from '@ngxs/store'
import { filter, finalize, map, Observable, startWith } from 'rxjs'
import { IMenuRouteInfo } from '../../interfaces/menu.interface'
import {
  ColorMode,
  FiltersState,
  RestoreFiltersFromLocalStorage,
  SetFilters
} from '@lla-platform/core/core-data-access'
import { FormControl } from '@angular/forms'
import { StateResetAll } from 'ngxs-reset-plugin'

export interface ISearchableMenuItem {
  label: string
  parentLabel?: string
  path: string
  queryParams?: Params
  clickFn?: () => void
}

@UntilDestroy()
@Component({
  selector: 'lla-main-menu',
  templateUrl: './main-menu.component.html',
  styleUrls: ['./main-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class MainMenuComponent implements OnInit {
  // Constants
  private readonly SUBMENU_PANEL_WIDTH = 260 // px
  private readonly SUBMENU_ITEM_HEIGHT = 40 // px
  private readonly SUBMENU_PANEL_PADDING = 48 // px
  private readonly VIEWPORT_PADDING = 8 // px

  @ViewChild('submenuPanel') submenuPanelRef?: ElementRef<HTMLElement>

  @Input() mainMenuRoutes: IMenuRouteInfo[] = []
  @Input() forDesktop = true
  @Input() isCollapsed = false

  @Output() menuItemClicked = new EventEmitter()
  @Output() collapsedChanged = new EventEmitter<boolean>()
  @Output() showLargeMenuChanged = new EventEmitter<boolean>()

  bookmarks$: Observable<IBookmarkItem[]> = inject(Store).select(BookmarksState.bookmarks)
  currentUser$ = inject(Store).select(UserAccountState.currentUser)
  colorMode$ = inject(Store).select(FiltersState.colorMode)

  deletingBookmarkId?: string
  mains: IMenuRouteInfo[] = []
  services: IMenuRouteInfo[] = []
  bookmarks: IMenuRouteInfo[] = []
  admins?: IMenuRouteInfo[] = []
  profileMenu: IMenuRouteInfo = {
    index: 9999,
    label: '',
    icon: 'user-circle-bold',
    subMenus: [
      {
        label: 'My Profile',
        icon: 'redo',
        path: '/profile/edit-profile'
      },
      {
        label: 'Announcements',
        icon: 'redo',
        path: '/announcements'
      },
      {
        label: 'Change Color',
        icon: 'redo',
        rightIcon: 'dark-mode',
        clickFn: () => this.changeColorMode()
      },
      {
        label: 'Log Out',
        icon: 'redo',
        clickFn: () => this.logout()
      }
    ]
  }
  flatBookmarksWithParent: IMenuRouteInfo[] = []

  expandedIndexes: Set<string> = new Set<string>()
  collapsedSections: Set<string> = new Set<string>()

  // Search
  @ViewChild(MatAutocompleteTrigger) searchAutoTrigger?: MatAutocompleteTrigger
  searchControl = new FormControl('')
  searchQuery = ''
  filteredSearchItems$!: Observable<ISearchableMenuItem[]>

  // Collapsed state properties
  hoveredItem: IMenuRouteInfo | null = null
  submenuPanelPosition: { top: string; left: string } | null = null
  submenuTriggerEl: HTMLElement | null = null

  constructor(
    private store: Store,
    public router: Router
  ) {}

  ngOnInit(): void {
    this.mainMenuRoutes = this.assignIndexesToMenuItems(this.mainMenuRoutes)
    this.mains = this.mainMenuRoutes.filter((el) => el.type === 'main')
    this.services = this.mainMenuRoutes.filter((el) => el.type === 'service')
    this.admins = this.mainMenuRoutes.filter((el) => el.type === 'admin')

    this.collapsedSections = new Set(this.store.selectSnapshot(FiltersState.collapsedMenuSections))
    this.expandActiveItems()

    this.bookmarks$.pipe(untilDestroyed(this)).subscribe((res) => {
      if (res) {
        this.addBookmarksMenuItems()
      }
    })

    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        untilDestroyed(this)
      )
      .subscribe(() => {
        this.expandActiveItems()
      })

    this.filteredSearchItems$ = this.searchControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        this.searchQuery = typeof value === 'string' ? value : ''
        return this.filterSearchItems(this.searchQuery)
      })
    )

    this.currentUser$.pipe(untilDestroyed(this)).subscribe((res) => {
      this.profileMenu.label = res ? `${res.firstName} ${res.lastName}` : ''
    })

    this.colorMode$.pipe(untilDestroyed(this)).subscribe((mode) => {
      const colorItem = this.profileMenu.subMenus?.find((q) => q.rightIcon)
      if (colorItem) {
        switch (mode) {
          case ColorMode.Dark:
            colorItem.label = 'Dark Mode'
            colorItem.rightIcon = 'dark-mode'
            break
          default:
            colorItem.label = 'Light Mode'
            colorItem.rightIcon = 'light-mode'
        }
      }
    })
  }

  private assignIndexesToMenuItems(items: IMenuRouteInfo[]): IMenuRouteInfo[] {
    return items.map((el, index) => ({
      ...el,
      index,
      subMenus: el.subMenus ? this.assignIndexesToMenuItems(el.subMenus) : undefined
    }))
  }

  expandActiveItems() {
    const allRoutes = [...this.mainMenuRoutes, ...(this.bookmarks ?? [])]
    this.expandMenuItemsRecursively(allRoutes)

    // Active route always wins visually; persisted state only changes on explicit header clicks
    if (this.admins?.some((el) => el.subMenus && this.hasActiveChild(el.subMenus))) {
      this.collapsedSections.delete('admin')
    }
  }

  toggleSection(section: 'bookmarks' | 'admin') {
    if (this.collapsedSections.has(section)) {
      this.collapsedSections.delete(section)
    } else {
      this.collapsedSections.add(section)
    }
    this.store.dispatch(
      new SetFilters({
        collapsedMenuSections: [...this.collapsedSections]
      })
    )
  }

  isSectionExpanded(section: string) {
    return !this.collapsedSections.has(section)
  }

  private expandMenuItemsRecursively(items: IMenuRouteInfo[], parentKey = '') {
    for (let index = 0; index < items.length; index++) {
      const element = items[index]
      const currentKey = this.getMenuItemKey(element, parentKey)

      if (element.subMenus) {
        if (this.hasActiveChild(element.subMenus)) {
          this.expandedIndexes.add(currentKey)
        }
        this.expandMenuItemsRecursively(element.subMenus, currentKey)
      }
    }
  }

  private hasActiveChild(subMenus: IMenuRouteInfo[]): boolean {
    return subMenus.some((el) => {
      if (el.path === location.pathname) {
        return true
      }
      if (el.subMenus) {
        return this.hasActiveChild(el.subMenus)
      }
      return false
    })
  }

  private getMenuItemKey(item: IMenuRouteInfo, parentKey = ''): string {
    return parentKey ? `${parentKey}-${item.index}` : `${item.index}`
  }

  changeCollapsableMenuStatus(item: IMenuRouteInfo, parentKey = '') {
    if (!item.subMenus) {
      return
    }
    const itemKey = this.getMenuItemKey(item, parentKey)

    if (!this.expandedIndexes.has(itemKey)) {
      this.expandedIndexes.add(itemKey)
    } else {
      if (item.type !== 'bookmark' && this.hasActiveChild(item.subMenus)) {
        return
      }
      this.expandedIndexes.delete(itemKey)
    }
  }

  isSubMenuVisible(item: IMenuRouteInfo, parentKey = '') {
    const itemKey = this.getMenuItemKey(item, parentKey)
    return item.subMenus && this.expandedIndexes.has(itemKey)
  }

  addBookmarksMenuItems() {
    const bookmarkItems = this.store.selectSnapshot(BookmarksState.bookmarks)
    const listPagesPath = this.store.selectSnapshot(FiltersState.listPagesPath)
    const allBookmarks: { [key: string]: IMenuRouteInfo[] } = {}
    for (const key of Object.keys(BookmarkType)) {
      if (bookmarkItems.find((el) => el.type === key)) {
        allBookmarks[key] = bookmarkItems
          .filter((el) => el.type === key)
          .map((el) => ({
            icon: 'redo',
            label: el.name,
            type: 'bookmark',
            path: listPagesPath[el.type],
            queryParams: {
              filters: el.token
            },
            extraInfo: el.id
          }))
      }
    }

    this.bookmarks = Object.keys(allBookmarks).map((el, index) => ({
      index: this.mainMenuRoutes.length + index,
      icon: 'bookmark',
      type: 'bookmark',
      label: BookmarkTypeToLabel[el],
      subMenus: allBookmarks[el]
    }))

    // Create flattened list with parent labels for collapsed mode
    this.flatBookmarksWithParent = Object.keys(allBookmarks).flatMap((key) =>
      allBookmarks[key].map((bookmark) => ({
        ...bookmark,
        label: `${BookmarkTypeToLabel[key]}: ${bookmark.label}`
      }))
    )
  }

  deleteBookmark(event: Event, id: string) {
    event.preventDefault()
    event.stopPropagation()

    if (this.deletingBookmarkId) {
      return
    }

    this.deletingBookmarkId = id
    this.store
      .dispatch(new DeleteBookmark(id))
      .pipe(
        untilDestroyed(this),
        finalize(() => (this.deletingBookmarkId = undefined))
      )
      .subscribe()
  }

  onMenuItemClick(item: IMenuRouteInfo) {
    if (!item) {
      return
    }

    if (item.path) {
      this.menuItemClicked.emit()
      if (this.hoveredItem) {
        this.closeSubmenuPanelImmediate()
      }
    }

    if (item.clickFn) {
      item.clickFn()
    }
  }

  onCollapsedMenuItemClick(item: IMenuRouteInfo, event: MouseEvent) {
    if (!this.isCollapsed || !item.subMenus || item.subMenus.length === 0) {
      return
    }

    const target = event.currentTarget as HTMLElement

    if (this.hoveredItem === item) {
      this.closeSubmenuPanelImmediate()
      return
    }

    this.submenuTriggerEl = target
    this.openSubmenuPanel(item, target)
  }

  isLastVisibleSubmenuItem(items: IMenuRouteInfo[], currentIndex: number): boolean {
    for (let i = currentIndex + 1; i < items.length; i++) {
      if (!items[i].hidden) return false
    }
    return true
  }

  private countVisibleSubmenuItems(items: IMenuRouteInfo[]): number {
    const visibleItems = items.filter((item) => !item.hidden)
    let count = 0
    for (let i = 0; i < visibleItems.length; i++) {
      const item = visibleItems[i]
      if (item.subMenus && item.subMenus.length > 0) {
        count += 1 // group header
        count += item.subMenus.filter((sub) => !sub.hidden).length
        if (i < visibleItems.length - 1) {
          count += 1 // separator (not for last visible group)
        }
      } else {
        count += 1
      }
    }
    return count
  }

  private openSubmenuPanel(item: IMenuRouteInfo, target: HTMLElement) {
    if (!item.subMenus || item.subMenus.length === 0) {
      return
    }

    const rect = target.getBoundingClientRect()
    const visibleCount = this.countVisibleSubmenuItems(item.subMenus)
    const panelHeight = Math.min(
      visibleCount * this.SUBMENU_ITEM_HEIGHT + this.SUBMENU_PANEL_PADDING,
      window.innerHeight * 0.8
    )

    let top = rect.top
    let left = rect.right

    // If panel would overflow right, flip to left
    if (left + this.SUBMENU_PANEL_WIDTH > window.innerWidth) {
      left = rect.left - this.SUBMENU_PANEL_WIDTH
    }

    // If panel would overflow bottom, shift up
    if (top + panelHeight > window.innerHeight) {
      top = Math.max(window.innerHeight - panelHeight - this.VIEWPORT_PADDING, this.VIEWPORT_PADDING)
    }

    this.hoveredItem = item
    this.submenuPanelPosition = {
      top: `${top}px`,
      left: `${left}px`
    }
  }

  private closeSubmenuPanelImmediate() {
    this.hoveredItem = null
    this.submenuPanelPosition = null
    this.submenuTriggerEl = null
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.hoveredItem) {
      return
    }

    const target = event.target as Node
    const panelEl = this.submenuPanelRef?.nativeElement
    const triggerEl = this.submenuTriggerEl

    if (panelEl?.contains(target) || triggerEl?.contains(target)) {
      return
    }

    this.closeSubmenuPanelImmediate()
  }

  hasActiveSubmenu(item: IMenuRouteInfo): boolean {
    if (!item.subMenus || item.subMenus.length === 0) {
      return false
    }
    return this.hasActiveChild(item.subMenus)
  }

  // --- Search ---

  private getAllSearchableItems(): ISearchableMenuItem[] {
    const items: ISearchableMenuItem[] = []
    const allGroups = [
      ...this.mains,
      ...this.services,
      ...(this.bookmarks ?? []),
      ...(this.admins ?? [])
    ]
    for (const group of allGroups) {
      this.flattenMenuItems(group, [], items)
    }
    return items
  }

  private flattenMenuItems(item: IMenuRouteInfo, parentLabels: string[], result: ISearchableMenuItem[]) {
    if (item.hidden) {
      return
    }
    if (item.path) {
      result.push({
        label: item.label,
        parentLabel: parentLabels.length > 0 ? parentLabels.join(' > ') : undefined,
        path: item.path,
        queryParams: item.queryParams,
        clickFn: item.clickFn
      })
    }
    if (item.subMenus) {
      const nextParents = [...parentLabels, item.label]
      for (const sub of item.subMenus) {
        this.flattenMenuItems(sub, nextParents, result)
      }
    }
  }

  private filterSearchItems(query: string): ISearchableMenuItem[] {
    const all = this.getAllSearchableItems()
    if (!query.trim()) {
      return all
    }
    const lower = query.toLowerCase()
    return all.filter(
      (item) =>
        item.label.toLowerCase().includes(lower) ||
        (item.parentLabel && item.parentLabel.toLowerCase().includes(lower))
    )
  }

  onSearchFocus() {
    // Always reset to empty string to show all items
    this.searchControl.setValue('')
    if (this.searchAutoTrigger) {
      this.searchAutoTrigger.openPanel()
    }
  }

  onSearchOptionSelected(item: ISearchableMenuItem) {
    if (item.clickFn) {
      item.clickFn()
    }
    this.router.navigate([item.path], { queryParams: item.queryParams })
    this.searchControl.setValue('')
    this.menuItemClicked.emit()
  }

  highlightMatch(text: string): { before: string; match: string; after: string } {
    if (!this.searchQuery.trim()) {
      return { before: text, match: '', after: '' }
    }
    const lower = text.toLowerCase()
    const idx = lower.indexOf(this.searchQuery.toLowerCase())
    if (idx === -1) {
      return { before: text, match: '', after: '' }
    }
    return {
      before: text.substring(0, idx),
      match: text.substring(idx, idx + this.searchQuery.length),
      after: text.substring(idx + this.searchQuery.length)
    }
  }

  displaySearchFn(): string {
    return ''
  }

  logout() {
    this.store.dispatch(new Logout()).subscribe(() => {
      this.router.navigateByUrl('/login')
      this.store.dispatch(new StateResetAll()).subscribe(() => {
        this.store.dispatch(new RestoreFiltersFromLocalStorage())
      })
    })
  }

  changeColorMode() {
    const colorMode = this.store.selectSnapshot(FiltersState.colorMode)
    let nextMode: ColorMode
    switch (colorMode) {
      case ColorMode.Light:
        nextMode = ColorMode.Dark
        break
      default:
        nextMode = ColorMode.Light
        break
    }
    this.store.dispatch(
      new SetFilters({
        colorMode: nextMode
      })
    )
  }
}
